import 'react-calendar/dist/Calendar.css';
import Layout from '../../components/layoutConfig/layout';
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { setupAPIclient } from '../../services/setupAPIclient';
import { FiMail, FiMessageCircle, FiSmartphone, FiUser } from 'react-icons/fi';
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min";
import 'animate.css';
import LocalStorage from '../../helpers/localstorage';

interface Permissions {
  sms: boolean;
  email: boolean;
  generalMessage: boolean;
}

function NotificationPage() {
  const localStorage = new LocalStorage();
  const token = String(localStorage.getAuthToken());
  const ApiClient = setupAPIclient(token);

  const [permissions, setPermissions] = useState<Permissions>({
    sms: false,
    email: false,
    generalMessage: false
  });
  const [generalText, setGeneralText] = useState<string>("");
  const [notificationId, setNotificationId] = useState<number | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);

  // Buscar notificação existente
  const handleGetNotification = async () => {
    try {
      setLoading(true);
      const response = await ApiClient.post("/management/get/notification");

      if (response?.data?.success?.length > 0) {
        const data = response.data.success[0];
        setNotificationId(data.id);
        setPermissions({
          sms: data.sms,
          email: data.email,
          generalMessage: data.a_message
        });
        setGeneralText(data.mensage || "");
      } else {
        setNotificationId(null);
      }
    } catch (error) {
      console.error(error);
      toast.error("Erro ao carregar configurações de notificação!");
    } finally {
      setLoading(false);
    }
  };

  // Criar nova notificação
  const handleCreateNotification = async () => {
    try {
      setSaving(true);
      const payload = {
        mensage: generalText,
        sms: permissions.sms,
        email: permissions.email,
        a_message: permissions.generalMessage
      };

      const response = await ApiClient.post("/management/create/notification", payload);
      if (response.data) {
        toast.success("Configurações criadas com sucesso!");
        handleGetNotification(); // Atualiza dados na tela
      }
    } catch (error) {
      console.error(error);
      toast.error("Erro ao criar configurações!");
    } finally {
      setSaving(false);
    }
  };

  // Atualizar notificação existente
  const handleUpdateNotification = async () => {
    if (!notificationId) return;
    try {
      setSaving(true);
      const payload = {
        id: notificationId,
        mensage: generalText,
        sms: permissions.sms,
        email: permissions.email,
        a_message: permissions.generalMessage
      };

      const response = await ApiClient.post("/management/update/notification", payload);
      if (response.data) {
        toast.success("Configurações atualizadas com sucesso!");
      }
    } catch (error) {
      console.error(error);
      toast.error("Erro ao atualizar configurações!");
    } finally {
      setSaving(false);
    }
  };

  // Lógica do botão principal
  const handleSaveNotification = () => {
    if (notificationId) {
      handleUpdateNotification();
    } else {
      handleCreateNotification();
    }
  };

  // Alternar switches
  const handleToggle = (type: keyof Permissions) => {
    setPermissions(prev => ({ ...prev, [type]: !prev[type] }));
  };

  useEffect(() => {
    handleGetNotification();
  }, []);

  return (
    <Layout>
      <div className="container py-5">
        <h2 className="mb-5 animate__animated animate__fadeInDown text-left" style={{ color: "#5dade2" }}>
          Configuração de Notificações
        </h2>
        <hr />

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status"></div>
            <p className="mt-3">Carregando configurações...</p>
          </div>
        ) : (
          <div className="row g-4">
            {/* Avatar */}
            <div className="col-md-3 text-center">
              <div className="card shadow-sm p-4 h-100 animate__animated animate__fadeInLeft hover-shadow bg-light">
                <FiUser size={80} className="text-secondary mb-3" />
                <h5 className="fw-semibold">Usuário</h5>
                <p className="text-muted small">
                  As permissões definidas aqui se aplicam às notificações do sistema.
                </p>
              </div>
            </div>

            {/* SMS */}
            <div className="col-md-3">
              <div className="card shadow-sm p-4 h-100 animate__animated animate__fadeInUp hover-shadow bg-light">
                <FiSmartphone size={50} className="text-success mb-3 hover-icon" title="Notificações por SMS" />
                <h5 className="fw-semibold">SMS</h5>
                <p className="text-muted small">
                  Ative para receber alertas importantes diretamente no seu celular.
                </p>
                <div className="form-check form-switch mt-2">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="smsSwitch"
                    checked={permissions.sms}
                    onChange={() => handleToggle("sms")}
                  />
                  <label className="form-check-label" htmlFor="smsSwitch">
                    Ativar SMS
                  </label>
                </div>
              </div>
            </div>

            {/* Email */}
            <div className="col-md-3">
              <div className="card shadow-sm p-4 h-100 animate__animated animate__fadeInRight hover-shadow bg-light">
                <FiMail size={50} className="text-info mb-3 hover-icon" title="Notificações por Email" />
                <h5 className="fw-semibold">Email</h5>
                <p className="text-muted small">
                  Ative para receber relatórios e avisos importantes diretamente no seu email.
                </p>
                <div className="form-check form-switch mt-2">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="emailSwitch"
                    checked={permissions.email}
                    onChange={() => handleToggle("email")}
                  />
                  <label className="form-check-label" htmlFor="emailSwitch">
                    Ativar Email
                  </label>
                </div>
              </div>
            </div>

            {/* Mensagem Geral */}
            <div className="col-md-3">
              <div className="card shadow-sm p-4 h-100 animate__animated animate__fadeInRight hover-shadow bg-light">
                <FiMessageCircle size={50} className="text-warning mb-3 hover-icon" title="Mensagem Geral" />
                <h5 className="fw-semibold">Mensagem Geral</h5>
                <p className="text-muted small">
                  Ative para enviar uma mensagem geral a todos os usuários quando necessário.
                </p>
                <div className="form-check form-switch mt-2">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="generalSwitch"
                    checked={permissions.generalMessage}
                    onChange={() => handleToggle("generalMessage")}
                  />
                  <label className="form-check-label" htmlFor="generalSwitch">
                    Ativar Mensagem
                  </label>
                </div>
                {permissions.generalMessage && (
                  <textarea
                    className="form-control mt-3 shadow-sm"
                    rows={4}
                    placeholder="Escreva a mensagem geral..."
                    value={generalText}
                    onChange={(e) => setGeneralText(e.target.value)}
                  />
                )}
              </div>
            </div>
          </div>
        )}

        {/* Botão Salvar */}
        <div className="text-center mt-5">
          <button
            onClick={handleSaveNotification}
            disabled={loading || saving}
            className="btn btn-danger btn-lg animate__animated animate__pulse px-5"
          >
            {saving
              ? "Salvando..."
              : notificationId
              ? "Atualizar Configurações"
              : "Salvar Configurações"}
          </button>
        </div>
      </div>

      {/* Estilo adicional */}
      <style jsx>{`
        .hover-shadow:hover {
          transform: translateY(-5px);
          transition: all 0.3s ease;
          box-shadow: 0 15px 25px rgba(0, 0, 0, 0.2);
        }
        .hover-icon:hover {
          transform: scale(1.3);
          transition: all 0.3s ease;
        }
        textarea.form-control {
          resize: none;
        }
      `}</style>
    </Layout>
  );
}

export default NotificationPage;
